/**
 * AuditKpiCard — KPI card for the Activity Audit feature.
 *
 * - Large metric (32px DM Sans)
 * - Prior-period delta with directional arrow (green up / red down / gray flat)
 * - Optional "(partial)" sub-label per §8.2
 * - Drillable: onClick navigates to /#/activity/drilldown?kpi=&window= (A4 LOCKED)
 * - No-data state: renders "—" per §8.1
 */
import { useLocation } from "wouter";
import { tokens } from "@/components/operator/tokens";
import { type TimeWindow } from "./audit-mock-data";

interface Props {
  label: string;
  value: number | null;        // null = no data → show "—"
  prior: number | null;
  isPartial?: boolean;
  kpiKey: string;
  window: TimeWindow;
  drillRole: string;
}

function deltaDir(curr: number | null, prior: number | null): "up" | "down" | "flat" {
  if (curr == null || prior == null) return "flat";
  if (curr > prior) return "up";
  if (curr < prior) return "down";
  return "flat";
}

function deltaValue(curr: number | null, prior: number | null): string {
  if (curr == null || prior == null) return "—";
  const diff = curr - prior;
  if (diff === 0) return "0";
  return diff > 0 ? `+${diff}` : `${diff}`;
}

export default function AuditKpiCard({
  label,
  value,
  prior,
  isPartial,
  kpiKey,
  window,
  drillRole,
}: Props) {
  const [, setLocation] = useLocation();
  const dir = deltaDir(value, prior);

  const arrowSymbol = dir === "up" ? "▲" : dir === "down" ? "▼" : "—";
  const deltaColor =
    dir === "up" ? tokens.color.success
    : dir === "down" ? tokens.color.danger
    : tokens.color.textSubtle;

  function handleDrilldown() {
    setLocation(`/activity/drilldown?kpi=${kpiKey}&window=${window}&role=${drillRole}`);
  }

  return (
    <div
      onClick={handleDrilldown}
      title={`Drilldown: ${label}`}
      style={{
        background: "#FFFFFF",
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        padding: "16px 18px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        fontFamily: tokens.font.body,
        minHeight: 120,
        cursor: "pointer",
        transition: "box-shadow 0.15s, border-color 0.15s",
        boxShadow: tokens.shadow.card,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = tokens.color.navy;
        (e.currentTarget as HTMLDivElement).style.boxShadow = "0 2px 8px rgba(15,23,42,0.1)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = tokens.color.border;
        (e.currentTarget as HTMLDivElement).style.boxShadow = tokens.shadow.card;
      }}
    >
      {/* Label */}
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: tokens.color.textMuted,
        }}
      >
        {label}
      </div>

      {/* Value */}
      <div
        style={{
          fontFamily: tokens.font.display,
          fontWeight: 700,
          fontSize: 32,
          lineHeight: 1.1,
          color: value == null ? tokens.color.textSubtle : tokens.color.textPrimary,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value == null ? "—" : value.toLocaleString()}
      </div>

      {/* Delta */}
      <div style={{ fontSize: 12, color: deltaColor, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
        <span>{arrowSymbol}</span>
        <span>{deltaValue(value, prior)} vs prior</span>
        {isPartial && (
          <span style={{ fontSize: 11, color: tokens.color.textSubtle, fontWeight: 400 }}>(partial)</span>
        )}
      </div>

      {/* Drilldown hint */}
      <div style={{ fontSize: 10, color: tokens.color.textSubtle, marginTop: 2, letterSpacing: "0.05em" }}>
        Click to drilldown
      </div>
    </div>
  );
}
